import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { type AISummary, AISummaryStatus } from "@/shared/domain/ai_summary";

import { sessionQueryKeys } from "./sessionQueryKeys";

/**
 * The session's AI summary, or `null` when none has been generated. Re-reads
 * a pending summary every `pendingPollRateMs` (default two seconds), so the
 * tab shows the text as soon as the agent writes it.
 */
export function useSessionSummary(
  sessionId: string,
  options?: { pendingPollRateMs?: number },
): UseQueryResult<AISummary | null, Error> {
  const { sessionService } = useDomainServices();
  const pendingPollRateMs = options?.pendingPollRateMs ?? 2000;
  return useQuery({
    queryKey: sessionQueryKeys.summary(sessionId),
    queryFn: ({ signal }) =>
      sessionService.getSummary(sessionId, { abortSignal: signal }),
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) =>
      query.state.data?.status === AISummaryStatus.Pending
        ? pendingPollRateMs
        : false,
  });
}
